<script lang="ts" setup>
import { CalendarDate } from '@internationalized/date';
import dayjs from 'dayjs';
import type { LineChartData } from '~/components/nrjx/chart/line/index.vue';

// ================================
// Composables
// ================================
const { t, locale } = useI18n();
const { add } = useToast();
const { getBaselineData, trainBaseline } = baselineRepository();
const { baselineData, baselineDataError } = storeToRefs(baselineStore());
const { getTailwindColor } = useTailwind();
const { params } = useRoute();

// Data
const loading = ref(false);
const state = reactive({
  startDate: new CalendarDate(2024, 1, 1),
  endDate: new CalendarDate(2024, 1, 1),
});

// ================================
// Methods
// ================================
const submit = async () => {
  try {
    loading.value = true;
    await trainBaseline({
      baselineId: Number(params.id),
      startDate: dayjs(state.startDate.toDate('UTC')).format('YYYY-MM-DD'),
      endDate: dayjs(state.endDate.toDate('UTC')).format('YYYY-MM-DD'),
    });
    navigateTo({ name: 'baseline-edit-review', params: { id: Number(params.id) } });
  } catch (error) {
    add({
      title: t('global.error'),
      description: t('global.error_description'),
      icon: 'i-lucide-circle-x',
      color: 'error',
    });
    throw error;
  } finally {
    loading.value = false;
  }
};

// ================================
// Computed
// ================================
const minStartDate = computed(() => {
  const startDate = dayjs(baselineData.value?.points.at(0)?.start_date ?? null);
  return new CalendarDate(startDate.year(), startDate.month() + 1, startDate.day());
});

const maxDate = computed(() => {
  const endDate = dayjs(baselineData.value?.points.at(-1)?.end_date ?? null);
  return new CalendarDate(endDate.year(), endDate.month() + 1, endDate.day());
});

const data = computed((): LineChartData => {
  return {
    keys: baselineData.value?.points.map((p) => dayjs(p.start_date).format('DD/MM/YYYY')) ?? [],
    lines: [
      {
        color: getTailwindColor('emerald-500'),
        dashed: false,
        values: baselineData.value?.points.map((p) => p.Y ?? 0) ?? [],
      },
    ],
    areas: [
      {
        label: `${t('baseline.training_period')}: ${trainingPeriodInPourcent.value}% ${t('baseline.of_available_data').toLowerCase()}`,
        color: getTailwindColor('gray-50'),
        borderColor: getTailwindColor('gray-500'),
        dashed: true,
        startDate: dayjs(state.startDate?.toString() ?? '').format('DD/MM/YYYY'),
        endDate: dayjs(state.endDate?.toString() ?? '').format('DD/MM/YYYY'),
      },
    ],
  };
});

const trainingPeriodInPourcent = computed(() => {
  if (!state.endDate || !state.startDate || !baselineData.value?.points.length) {
    return 0;
  }

  const startDateStr = dayjs(state.startDate.toDate('UTC')).format('YYYY-MM-DD');
  const endDateStr = dayjs(state.endDate.toDate('UTC')).format('YYYY-MM-DD');

  // Trouver les index des points de début et fin
  const startIndex = baselineData.value.points.findIndex((point) => dayjs(point.start_date).format('YYYY-MM-DD') >= startDateStr);
  const endIndex = baselineData.value.points.findIndex((point) => dayjs(point.end_date).format('YYYY-MM-DD') >= endDateStr);
  const totalPoints = baselineData.value.points.length;

  if (startIndex === -1 || endIndex === -1) {
    return 0;
  }

  const pointsInPeriod = endIndex - startIndex + 1;

  return Math.round((pointsInPeriod / totalPoints) * 100);
});

// ================================
// Lifecycle
// ================================
onMounted(async () => {
  loading.value = true;
  baselineData.value = undefined;
  await getBaselineData(Number(params.id));

  const startDate = dayjs(baselineData.value?.points.at(0)?.start_date ?? null);

  // Calculer l'index qui correspond exactement à 80% des points
  const totalPoints = baselineData.value?.points.length ?? 0;
  const targetPoints = Math.ceil(totalPoints * 0.8); // Nombre de points voulus (80%)
  const endIndex = targetPoints - 1; // -1 car les index commencent à 0
  const endDate = dayjs(baselineData.value?.points.at(endIndex)?.end_date ?? null);

  if (startDate && endDate) {
    state.startDate = new CalendarDate(startDate.year(), startDate.month() + 1, startDate.day());
    state.endDate = new CalendarDate(endDate.year(), endDate.month() + 1, endDate.day());
  }
  loading.value = false;
});
</script>
<template>
  <div>
    <div class="w-full bg-white rounded-lg p-4 border border-gray-200">
      <!-- Title -->
      <h2 class="text-lg font-bold">
        {{ $t('baseline.training_period_title') }}
      </h2>
      <p class="text-sm text-gray-500">
        {{ $t('baseline.training_period_description') }}
      </p>

      <template v-if="!baselineDataError">
        <div class="grid grid-cols-2 gap-4 mt-4">
          <u-form-field :label="$t('baseline.training_period_start_date')">
            <u-popover
              class="w-full"
              :content="{
                align: 'end',
                side: 'bottom',
              }"
            >
              <u-button color="neutral" variant="outline" icon="i-lucide-calendar">
                {{ state.startDate ? dayjs(state.startDate.toDate('UTC')).format('DD/MM/YYYY') : $t('global.select_date') }}
              </u-button>

              <template #content>
                <u-calendar v-model="state.startDate" :min-value="minStartDate" :max-value="state.endDate" :locale="locale" class="p-2" />
              </template>
            </u-popover>
          </u-form-field>

          <u-form-field :label="$t('baseline.training_period_end_date')">
            <u-popover
              class="w-full"
              :content="{
                align: 'end',
                side: 'bottom',
              }"
            >
              <u-button color="neutral" variant="outline" icon="i-lucide-calendar">
                {{ state.endDate ? dayjs(state.endDate.toDate('UTC')).format('DD/MM/YYYY') : $t('global.select_date') }}
              </u-button>

              <template #content>
                <u-calendar v-model="state.endDate" :min-value="state.startDate" :max-value="maxDate" :locale="locale" class="p-2" />
              </template>
            </u-popover>
          </u-form-field>
        </div>

        <u-alert
          v-if="trainingPeriodInPourcent > 90"
          color="warning"
          variant="subtle"
          class="mt-4"
          :title="$t('baseline.training_period_alert_error_title')"
          :description="$t('baseline.training_period_alert_error_description')"
        />

        <nrjx-chart-line
          v-if="baselineData || loading"
          :loading="loading"
          :data="data"
          :options="{ height: 'h-[500px]', unit: 'kWh' }"
          class="mt-4"
        />
      </template>
      <u-alert v-else color="warning" variant="subtle" class="mt-4" :title="$t('error.' + baselineDataError)" />
    </div>

    <div class="w-full mt-8 flex flex-row justify-between">
      <u-button color="neutral" variant="outline" :label="$t('global.cancel')" @click="navigateTo('/references/baselines')" />
      <div class="flex items-center gap-2">
        <u-button color="neutral" variant="outline" to="/references/baselines/create/model-selection" :label="$t('global.previous')" />
        <u-button
          color="primary"
          variant="solid"
          :disabled="trainingPeriodInPourcent > 90 || loading || !!baselineDataError"
          :label="$t('global.next')"
          @click="submit"
        />
      </div>
    </div>
  </div>
</template>
